import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import BroadCastInformations from "./BroadcastInformations";
import Schedules from "./Schedules";
import useGetSchedulesQuery from "../../../hooks/queries/useGetSchedulesQuery";
import useFASTLayout from "../../../hooks/useFASTLayout";
import CircularSpinner from "../../utils/loaderEditor/CircularSpinner";
import ChannelClock from "../scheduling/ChannelClock";

import type { JSX } from "react";

import type {
  OVPBroadcast,
  Schedule,
  ScheduleBroadcast,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

function Destinations(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const [activeBroadcastId, setActiveBroadcastId] = useState<string | null>(
    null,
  );
  const [activeScheduleId, setActiveScheduleId] = useState<string>("");

  const { data: schedules, isLoading: isLoadingSchedules } =
    useGetSchedulesQuery({ channelId });

  const scheduleList = useMemo(() => schedules?.data ?? [], [schedules?.data]);

  useEffect(() => {
    if (!activeBroadcastId || !activeScheduleId) {
      const defaultSchedule = schedules?.data.find(
        (schedule) => schedule.default,
      );
      setActiveBroadcastId(defaultSchedule?.broadcasts?.[0]?.uuid ?? null);
      setActiveScheduleId(defaultSchedule?.uuid ?? "");
    }
  }, [activeBroadcastId, activeScheduleId, scheduleList, schedules?.data]);

  useFASTLayout({
    title: t("distribute"),
    button: <ChannelClock />,
  });

  const activeBroadcastAndSchedule: {
    broadcast: ScheduleBroadcast | OVPBroadcast;
    schedule: Schedule;
  } | null = useMemo(
    () =>
      scheduleList.reduce(
        (
          acc: {
            broadcast: ScheduleBroadcast | OVPBroadcast;
            schedule: Schedule;
          } | null,
          schedule,
        ) => {
          const activeBroadcast = schedule.broadcasts.find(
            (broadcast) => broadcast.uuid === activeBroadcastId,
          );

          return activeBroadcast
            ? { broadcast: activeBroadcast, schedule }
            : acc;
        },
        null,
      ),
    [activeBroadcastId, scheduleList],
  );

  return (
    <div className="flex w-full gap-[0.78125rem]">
      {isLoadingSchedules ? (
        <div className="m-[3.125rem] flex size-full items-center justify-center">
          <CircularSpinner loading />
        </div>
      ) : (
        <React.Fragment>
          <Schedules
            activeBroadcastId={activeBroadcastId}
            schedules={scheduleList}
            setActiveBroadcastId={setActiveBroadcastId}
            setActiveScheduleId={setActiveScheduleId}
          />
          <BroadCastInformations
            key={activeScheduleId}
            activeScheduleId={activeScheduleId}
            broadcast={activeBroadcastAndSchedule?.broadcast}
            scheduleHlsUrl={activeBroadcastAndSchedule?.schedule.hls_url}
          />
        </React.Fragment>
      )}
    </div>
  );
}

export default Destinations;
